// Position-in-field section of the Argument Critique slide-over.
// Locked states render inline; status comes from computeCritiqueStatuses via the panel.

import { useState } from "react";
import {
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Loader2,
  RefreshCw,
  ThumbsDown,
  ThumbsUp,
} from "lucide-react";
import { usePositionAnalysis } from "@/hooks/usePositionAnalysis";
import type { PositionAnalysis } from "@/hooks/usePositionAnalysis";
import type { ToolStatusResult } from "@/lib/analyzeToolState";
import { useCurrentUser, isPaidPlan } from "@/hooks/useCurrentUser";
import { trackEvent } from "@/lib/engagement";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const NOVELTY_COLORS: Record<string, string> = {
  derivative: "bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300",
  incremental: "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300",
  novel: "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300",
  paradigm_challenging:
    "bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300",
};

function normalizeConfidence(val: unknown): number {
  if (typeof val === "number") return val > 1 ? val / 100 : val;
  const str = String(val ?? "").toLowerCase();
  if (str === "high") return 0.9;
  if (str === "medium") return 0.6;
  if (str === "low") return 0.3;
  const parsed = parseFloat(str);
  return isNaN(parsed) ? 0.5 : parsed > 1 ? parsed / 100 : parsed;
}

const STANCE_LABELS: Record<string, string> = {
  sides_with_a: "Sides with A",
  sides_with_b: "Sides with B",
  bridges: "Bridges",
  novel: "Novel stance",
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface Props {
  conceptId: string;
  positionAnalysis: PositionAnalysis | null;
  status: ToolStatusResult;
}

export function PositionSection({ conceptId, positionAnalysis, status }: Props) {
  const { analyze, submitFeedback } = usePositionAnalysis(conceptId);
  // Backend is require_paid — a downgraded user can still VIEW a cached
  // result (cached-beats-lock), but rerun affordances would only 403.
  const { data: currentUser } = useCurrentUser();
  const isPaid = isPaidPlan(currentUser?.plan);

  const [feedbackGiven, setFeedbackGiven] = useState(false);
  const [tensionsOpen, setTensionsOpen] = useState(false);
  const [gapsOpen, setGapsOpen] = useState(false);

  // isAnalyzing: either the server job is running or the mutation is in-flight
  const isAnalyzing = status.status === "running" || analyze.isPending;

  // ---------------------------------------------------------------------------
  // Normalize LLM output
  // ---------------------------------------------------------------------------
  const raw = positionAnalysis?.result ?? null;
  const result = raw
    ? {
        ...raw,
        primary_alignment: {
          ...raw.primary_alignment,
          // LLM returns camp (not camp_label), confidence as string
          camp_label:
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (raw.primary_alignment as any).camp_label ??
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (raw.primary_alignment as any).camp ??
            "Unknown",
          confidence: normalizeConfidence(raw.primary_alignment.confidence),
        },
        // LLM may return object or string
        positioning_statement:
          typeof raw.positioning_statement === "string"
            ? raw.positioning_statement
            : typeof raw.positioning_statement === "object" &&
                raw.positioning_statement !== null
              ? JSON.stringify(raw.positioning_statement)
              : String(raw.positioning_statement ?? ""),
        // LLM returns {level, explanation} — extract level string
        novelty_assessment:
          typeof raw.novelty_assessment === "object" &&
          raw.novelty_assessment !== null
            ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
              ((raw.novelty_assessment as any).level as string) ?? "unknown"
            : String(raw.novelty_assessment ?? "unknown"),
        // Gaps may be strings instead of {blind_spot, how_addressed} objects
        gaps_addressed: (raw.gaps_addressed ?? []).map(
          (g: string | { blind_spot: string; how_addressed: string }) =>
            typeof g === "string" ? { blind_spot: g, how_addressed: "" } : g,
        ),
      }
    : null;

  // ---------------------------------------------------------------------------
  // Feedback handler
  // ---------------------------------------------------------------------------
  const handleFeedback = (signal: "thumbs_up" | "thumbs_down") => {
    if (feedbackGiven) return;
    setFeedbackGiven(true);
    submitFeedback.mutate({ signal });
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <section className="rounded-md border border-border p-3 space-y-3">
      {/* Header row */}
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Position in Field
        </h3>
        {result && !isAnalyzing && isPaid && (
          <button
            onClick={() => {
              setFeedbackGiven(false);
              analyze.mutate();
            }}
            disabled={analyze.isPending}
            className="inline-flex items-center gap-1 rounded p-1 text-[11px] text-muted-foreground hover:text-foreground disabled:opacity-50"
            aria-label="Rerun position analysis"
          >
            <RefreshCw className={`h-3 w-3 ${analyze.isPending ? "animate-spin" : ""}`} />
          </button>
        )}
      </div>

      {/* Locked — no result yet */}
      {status.status === "locked" && !result && (
        <p className="py-2 text-xs text-muted-foreground">
          🔒 {status.lockedReason}
        </p>
      )}

      {/* Never run — ready state */}
      {status.status === "ready" && !result && !isAnalyzing && (
        <div className="flex flex-col items-center gap-2 py-4">
          <p className="text-xs text-muted-foreground text-center">
            Analyze where your claims sit within the field debate.
          </p>
          <button
            onClick={() => analyze.mutate()}
            disabled={analyze.isPending}
            className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
            data-guide-target="positionAnalysisButton"
          >
            Analyze position
          </button>
        </div>
      )}

      {/* Loading skeleton */}
      {isAnalyzing && (
        <div className="space-y-2 py-4">
          <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Analyzing position...
          </div>
          <div className="space-y-1.5">
            <div className="h-3 w-3/4 animate-pulse rounded bg-muted" />
            <div className="h-3 w-1/2 animate-pulse rounded bg-muted" />
            <div className="h-3 w-2/3 animate-pulse rounded bg-muted" />
          </div>
        </div>
      )}

      {/* Stale banner */}
      {status.status === "stale" && result && !isAnalyzing && (
        <div
          role="status"
          aria-live="polite"
          data-testid="position-stale-banner"
          className="flex items-center justify-between gap-2 rounded border border-amber-500/30 bg-amber-500/10 px-2 py-1.5 text-[11px] text-amber-700 dark:text-amber-200"
        >
          <div className="flex items-center gap-1.5 min-w-0">
            <AlertTriangle className="size-3.5 shrink-0" />
            <span className="truncate">
              Your claims or evidence changed since this ran.
            </span>
          </div>
          {isPaid && (
          <button
            onClick={() => {
              trackEvent("analyze_stale_rerun_click", {
                tool: "position",
                overlap_percent: 0,
              });
              setFeedbackGiven(false);
              analyze.mutate();
            }}
            disabled={analyze.isPending}
            className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 font-medium hover:bg-amber-500/15 disabled:opacity-50"
            aria-label="Rerun analysis on current scope"
          >
            <RefreshCw
              className={`size-3 ${analyze.isPending ? "animate-spin" : ""}`}
            />
            Rerun
          </button>
          )}
        </div>
      )}

      {/* Result body */}
      {result && !isAnalyzing && (
        <>
          {/* Primary alignment */}
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium">
                {result.primary_alignment.camp_label}
              </span>
              <span className="text-[11px] text-muted-foreground">
                {Math.round(result.primary_alignment.confidence * 100)}%
                confidence
              </span>
            </div>
            <p className="text-xs text-muted-foreground italic">
              {result.positioning_statement}
            </p>
          </div>

          {/* Novelty badge */}
          <div>
            <span
              className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-medium ${
                NOVELTY_COLORS[result.novelty_assessment] ??
                NOVELTY_COLORS.incremental
              }`}
            >
              {result.novelty_assessment.replace(/_/g, " ")}
            </span>
          </div>

          {/* Tensions engaged */}
          {result.tensions_engaged.length > 0 && (
            <div>
              <button
                onClick={() => setTensionsOpen(!tensionsOpen)}
                className="flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground"
              >
                {tensionsOpen ? (
                  <ChevronDown className="h-3 w-3" />
                ) : (
                  <ChevronUp className="h-3 w-3" />
                )}
                Tensions ({result.tensions_engaged.length})
              </button>
              {tensionsOpen && (
                <ul className="mt-1 space-y-1.5 pl-4">
                  {result.tensions_engaged.map((t, i) => (
                    <li key={i} className="text-xs">
                      <span className="font-medium">
                        {t.camp_a} vs {t.camp_b}
                      </span>
                      <span className="ml-1 text-muted-foreground">
                        — {STANCE_LABELS[t.stance] ?? t.stance}
                      </span>
                      <p className="text-muted-foreground">{t.explanation}</p>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {/* Gaps addressed */}
          {result.gaps_addressed.length > 0 && (
            <div>
              <button
                onClick={() => setGapsOpen(!gapsOpen)}
                className="flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground"
              >
                {gapsOpen ? (
                  <ChevronDown className="h-3 w-3" />
                ) : (
                  <ChevronUp className="h-3 w-3" />
                )}
                Gaps Addressed ({result.gaps_addressed.length})
              </button>
              {gapsOpen && (
                <ul className="mt-1 space-y-1.5 pl-4">
                  {result.gaps_addressed.map((g, i) => (
                    <li key={i} className="text-xs">
                      <span className="font-medium">{g.blind_spot}</span>
                      <p className="text-muted-foreground">{g.how_addressed}</p>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {/* Feedback row */}
          <div className="flex items-center gap-3 pt-1 border-t border-border">
            <div className="flex items-center gap-1">
              <button
                onClick={() => handleFeedback("thumbs_up")}
                disabled={feedbackGiven}
                className="rounded p-1 text-muted-foreground hover:text-foreground disabled:opacity-40 disabled:cursor-not-allowed"
                title="Helpful"
              >
                <ThumbsUp className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={() => handleFeedback("thumbs_down")}
                disabled={feedbackGiven}
                className="rounded p-1 text-muted-foreground hover:text-foreground disabled:opacity-40 disabled:cursor-not-allowed"
                title="Not helpful"
              >
                <ThumbsDown className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        </>
      )}
    </section>
  );
}
